import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ErrorCodes } from 'xosign-types';
import { HOME_ROUTE } from '@/config';
import useApi from '@/hooks/useApi';
import useEmailFieldValidation from '@/hooks/useEmailFieldValidation';
import useApp from '@/pages/App.context';
import Button from '@/components/Button';
import FormInput from '@/components/FormInput';
import FormError from '@/components/FormError';
import Spinner from '@/components/Spinner';
import ConsentCheckbox from '@/pages/Auth/ConsentCheckbox';
import styles from '@/pages/Auth/Auth.module.scss';
import idAustriaStyles from './IdAustria.module.scss';

type IdAustriaRegisterInputs = {
  email: string;
};

const IdAustria = () => {
  const { t, i18n } = useTranslation(['onboarding', 'auth']);
  const { setAppTitle } = useApp();
  const [idAustriaSync, { response, error, loading }] = useApi(
    'users.login.idAustria'
  );
  const [idAustriaFinish, { response: responseFinish, error: errorFinish }] =
    useApi('users.login.idAustria.finish');
  const navigate = useNavigate();
  const code = new URLSearchParams(window.location.search).get('code') ?? '';
  const [consentGiven, setConsentGiven] = useState(false);

  const signupPendingId =
    error?.statusCode === 401 &&
    error.response.code === 'EMissingEmail' &&
    error.response.signupId
      ? error.response.signupId
      : null;

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
    getValues,
  } = useForm<IdAustriaRegisterInputs>();

  const finishErrorMessage = errorFinish
    ? errorFinish.statusCode === 409 &&
      errorFinish.response.code === ErrorCodes.EUserAlreadyExists
      ? t('auth:register.userRegistered')
      : errorFinish.response.message
    : undefined;

  const { emailRegistration, handleEmailBlur, handleEmailChange } =
    useEmailFieldValidation<IdAustriaRegisterInputs>({
      fieldName: 'email',
      register,
      requiredMessage: t('auth:fields.emailRequired'),
      patternMessage: t('auth:fields.emailValidity'),
      setValue,
      trigger,
    });

  const doRegister: SubmitHandler<IdAustriaRegisterInputs> = (formInputs) => {
    if (!signupPendingId || !consentGiven) {
      return;
    }

    void idAustriaFinish({
      email: formInputs.email,
      id: signupPendingId,
      language: i18n.language,
    });
  };

  const handleConsentToggle = () => {
    setConsentGiven((prevState) => !prevState);
  };

  // ID Austria auth codes are single-use. React 18 StrictMode in dev double-
  // invokes mount effects, which would consume the code on the first run and
  // hit `invalid_grant` on the second. Guard with a ref so the exchange fires
  // exactly once per mount.
  const syncedRef = useRef(false);
  useEffect(() => {
    if (syncedRef.current || !code) {
      return;
    }
    syncedRef.current = true;
    const redirectUri = `${window.location.origin}/auth/register/idaustria/callback`;
    void idAustriaSync({ code, redirectUri });
  }, [code, idAustriaSync]);

  useEffect(() => {
    if (response) {
      if (!response.success) {
        void navigate('/auth/login');
        return;
      }

      localStorage.setItem('token', response.token);
      sessionStorage.setItem('token', response.token); // In case this login is from signing flow

      void navigate(HOME_ROUTE);
    }
  }, [response, navigate]);

  useEffect(() => {
    if (error) {
      if (
        error.statusCode === 401 &&
        error.response.code === 'EMissingEmail' &&
        error.response.signupId
      ) {
        // signupPendingId is derived from error — no action needed here
        return;
      }

      if (error.statusCode === 401 && error.response.code === 'AgeError') {
        void navigate('/auth/login?error=age');
        return;
      }

      void navigate('/auth/login?error=general');
    }
  }, [code, error, idAustriaSync, navigate]);

  useEffect(() => {
    if (responseFinish?.token) {
      localStorage.setItem('token', responseFinish.token);

      // Not yet replacing the URL on purpose. This way we can always go back
      // to the beginning when the back button was clicked accidentally.
      void navigate('/auth/register/verify-email', {
        state: { email: getValues('email') },
      });
    }
  }, [getValues, navigate, responseFinish?.token]);

  useEffect(() => {
    setAppTitle(t('auth:idAustria.title'));
  }, [setAppTitle, t]);

  if (signupPendingId) {
    return (
      <>
        <h2 className={styles.heading}>{t('auth:idAustria.title')}</h2>
        <div className={styles.text}>{t('auth:idAustria.desc')}</div>
        <form onSubmit={handleSubmit(doRegister)} className={styles.form}>
          <FormInput
            autoComplete="email"
            errorMessage={errors.email?.message}
            id="email"
            label={t('auth:fields.email')}
            placeholder={t('auth:fields.emailPlaceholder')}
            type="email"
            {...emailRegistration}
            onBlur={handleEmailBlur}
            onChange={handleEmailChange}
          />
          {finishErrorMessage && (
            <FormError isRoot message={finishErrorMessage} />
          )}
          <ConsentCheckbox
            checked={consentGiven}
            onToggle={handleConsentToggle}
          />
          <Button
            className={styles.button}
            submit
            loading={loading}
            disabled={!consentGiven}
          >
            {t('auth:register.submit')}
          </Button>
        </form>
      </>
    );
  }

  return (
    <div
      className={idAustriaStyles.centerLoader}
      aria-label={t('onboarding:idAustria.loading')}
    >
      <Spinner />
    </div>
  );
};

export default IdAustria;
